import message from "components/CustomMessage";
import { filter, isEmpty, noop } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { CancellableAPIRequest } from "restAPI";

type Response<T> = T[] | { content: T[] } | { projects: T[] };

export default function useLocalPagination<T>(
  api: () => CancellableAPIRequest<Response<T>>,
) {
  const [data, setData] = useState<T[]>();

  const fetchData = useCallback(() => {
    const { promise, cancel } = api();
    promise
      .then((response: Response<T>) => {
        let projects: T[] = [];
        if (!isEmpty(response)) {
          if (Array.isArray(response)) {
            projects = response;
          } else if ("content" in response) {
            projects = response.content;
          } else if ("projects" in response) {
            projects = response.projects;
          }
        }
        setData(projects);
      })
      .catch((err) => {
        message.error("Problem fetching projects");
      });
    return cancel;
  }, [api]);

  useEffect(() => {
    if (!data) {
      const cancel = fetchData();
      return cancel;
    }
  }, [fetchData, data]);

  const getPageData = useCallback(
    (params: { page: number; size: number; name?: string }) => {
      const { page, size, name } = params;
      const promise = new Promise((resolve) => {
        let finalData: T[] = data || [];
        if (name) {
          const filteredData = filter(
            data,
            (project: { name: string; id: string }) =>
              project.name.toLowerCase().includes(name.toLowerCase()),
          ) as T[];
          finalData = filteredData;
        }
        finalData = finalData?.slice(page * size, (page + 1) * size);
        resolve(finalData);
      });
      const cancel = noop;
      //Return a promise which resolves to finalData
      //and noop cancel to match SelectWithPaginated's 'api' format.
      return { promise, cancel };
    },
    [data],
  );

  return {
    getPageData,
    finalData: data,
  };
}
